import { useState } from "react";
import { PROBLEM_CODES, api, errorMessage, problemCode, useApiMutation } from "../../api";
import type { Grid } from "../../api";
import { Button, Card, ConfirmDialog, Field, useToast } from "../../components";
import { lignesDepuisGrille, validerEdition } from "./EditionGrille";
import type { LigneEdition, ResultatEdition } from "./EditionGrille";
import styles from "./EditionGrille.module.css";

export type EditionGrilleCarteProps = {
  grille: Grid;
  onPubliee: (g: Grid) => void;
  onAnnuler: () => void;
};

/**
 * Édition de la grille par l'administrateur ESN. On part de la grille en
 * vigueur, on ajoute, renomme, retarife ou retire des expertises, puis on
 * publie la version suivante, complète. Rien n'est modifié en place : les
 * missions déjà commandées gardent les tarifs de leur version.
 */
export function EditionGrilleCarte({ grille, onPubliee, onAnnuler }: EditionGrilleCarteProps) {
  const { notify } = useToast();
  const publication = useApiMutation(api.publishGrid);
  const [libelle, setLibelle] = useState(grille.label);
  const [lignes, setLignes] = useState<LigneEdition[]>(() => lignesDepuisGrille(grille));
  const [resultat, setResultat] = useState<ResultatEdition | null>(null);
  const [confirmer, setConfirmer] = useState(false);
  const [suivante, setSuivante] = useState(0);

  const erreurs = resultat !== null && !resultat.ok ? resultat : null;

  function modifier(cle: string, champ: "libelle" | "tarif", valeur: string) {
    setLignes((ls) => ls.map((l) => (l.cle === cle ? { ...l, [champ]: valeur } : l)));
  }

  function ajouter() {
    setLignes((ls) => [...ls, { cle: `nouvelle-${suivante}`, code: "", libelle: "", tarif: "" }]);
    setSuivante((n) => n + 1);
  }

  function demanderPublication() {
    const r = validerEdition(grille.version, libelle, lignes);
    setResultat(r);
    if (r.ok) {
      setConfirmer(true);
    }
  }

  async function publier() {
    if (resultat === null || !resultat.ok) {
      return;
    }
    const publiee = await publication.run(resultat.requete);
    setConfirmer(false);
    if (publiee === null) {
      // Le refus s'affiche sous le formulaire, lu au rendu suivant : juste
      // après `run`, `publication.error` vaut encore celui d'avant.
      return;
    }
    notify({
      tone: "success",
      title: `Grille v${publiee.version} publiée`,
      description: "Elle s'applique aux prochaines commandes ; les missions en cours gardent leurs tarifs.",
    });
    onPubliee(publiee);
  }

  return (
    <Card
      title={`Préparer la version v${grille.version + 1}`}
      subtitle="La grille est publiée complète : ce qui est retiré ici ne pourra plus être commandé, ce qui est retarifé ne vaut que pour les prochaines commandes."
    >
      <div className={styles.edition}>
        <Field label="Nom de la version" required error={erreurs?.libelle}>
          {(commande) => <input {...commande} value={libelle} onChange={(e) => setLibelle(e.target.value)} maxLength={200} />}
        </Field>

        <ol className={styles.lignes}>
          {lignes.map((l) => (
            <li key={l.cle} className={styles.ligne}>
              <Field
                label="Expertise"
                required
                error={erreurs?.lignes[l.cle]?.libelle}
                hint={l.code !== "" ? <span className={styles.code}>{l.code}</span> : undefined}
              >
                {(commande) => (
                  <input {...commande} value={l.libelle} onChange={(e) => modifier(l.cle, "libelle", e.target.value)} maxLength={200} />
                )}
              </Field>
              <Field label="Tokens / jour" required error={erreurs?.lignes[l.cle]?.tarif}>
                {(commande) => (
                  <input
                    {...commande}
                    inputMode="decimal"
                    value={l.tarif}
                    onChange={(e) => modifier(l.cle, "tarif", e.target.value)}
                  />
                )}
              </Field>
              <Button
                variant="ghost"
                size="sm"
                className={styles.retirer}
                onClick={() => setLignes((ls) => ls.filter((x) => x.cle !== l.cle))}
                aria-label={`Retirer ${l.libelle || "cette ligne"}`}
              >
                Retirer
              </Button>
            </li>
          ))}
        </ol>

        {publication.error !== null && (
          <p className={styles.alerte} role="alert">
            {problemCode(publication.error) === PROBLEM_CODES.gridConflict
              ? "Une autre version de la grille vient d'être publiée. Annulez, relisez la grille en vigueur, puis reprenez vos modifications."
              : errorMessage(publication.error)}
          </p>
        )}

        {erreurs?.globale !== undefined && (
          <p className={styles.alerte} role="alert">
            {erreurs.globale}
          </p>
        )}

        <div className={styles.actions}>
          <Button variant="secondary" onClick={ajouter}>
            Ajouter une expertise
          </Button>
          <Button variant="primary" onClick={demanderPublication} loading={publication.pending}>
            Publier la v{grille.version + 1}
          </Button>
          <Button variant="ghost" onClick={onAnnuler} disabled={publication.pending}>
            Annuler
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmer}
        title={`Publier la grille v${grille.version + 1} ?`}
        description={`${resultat?.ok ? resultat.requete.expertises.length : 0} expertises. La version publiée ne se modifie plus : pour changer un tarif, on publie la suivante.`}
        confirmLabel="Publier"
        pending={publication.pending}
        onConfirm={() => void publier()}
        onCancel={() => setConfirmer(false)}
      />
    </Card>
  );
}
